/**
 * Demo payments (development / testing only).
 *
 * HISTORY (functional validation defect C-02)
 * -------------------------------------------
 * Card 4242 4242 4242 4242 used to be "authorized" and "captured" inside the
 * browser in EVERY build, with no gateway call and no server hold. The
 * summary then read "Paid" while the server captured nothing, and the API
 * started the session anyway. The API now refuses to start a card-billed
 * session without an authorized hold (402 PAYMENT_HOLD_REQUIRED), and the
 * browser never fakes an authorization or a capture any more.
 *
 * THE RULE
 * --------
 * Demo payments are ON only when BOTH hold at build time:
 *   - VITE_DEMO_PAYMENTS_ENABLED is exactly 'true', and
 *   - the Vite mode is not 'production'.
 * `vite build` / `yarn build` (every deploy, the Dockerfile, and the local
 * functional harness) run in mode 'production', so a production bundle has
 * demo payments OFF whatever the flag says — and scripts/assert-build-env.mjs
 * refuses to build one with the flag set, so the mistake fails loudly instead
 * of being silently ignored. `vite` (dev server, mode 'development') and
 * vitest (mode 'test') can turn it on.
 *
 * When ON, the demo card is still authorized by the SERVER: it goes through
 * the direct pre-authorization route (POST /ev/sessions/:uid/payment/preauth),
 * which is only meaningful against the API's mock gateway. That produces a
 * real hold, so /start, capture and the summary behave exactly as for a real
 * card. When OFF, the demo card is an ordinary card (3-D Secure path) and no
 * demo hint is shown.
 */

/** Demo card number (a standard test PAN; never a real card). */
export const DEMO_CARD_NUMBER = '4242424242424242';

/**
 * @param {Record<string, unknown>} [env] Vite env (defaults to import.meta.env)
 * @returns {boolean}
 */
export function isDemoPaymentsEnabled(env = import.meta.env) {
  return env?.VITE_DEMO_PAYMENTS_ENABLED === 'true' && env?.MODE !== 'production';
}

/**
 * Normalize card number by removing spaces and dashes
 * @param {string} cardNumber - Raw card number input
 * @returns {string} Normalized card number (digits only)
 */
export function normalizeCardNumber(cardNumber) {
  if (!cardNumber) return '';
  return cardNumber.replace(/[\s-]/g, '');
}

/**
 * Whether this card takes the demo path. Always false unless demo payments
 * are enabled for this build (see THE RULE above).
 * @param {string} cardNumber - Card number to check (can include spaces/dashes)
 * @param {Record<string, unknown>} [env] Vite env (defaults to import.meta.env)
 * @returns {boolean}
 */
export function isDemoCard(cardNumber, env = import.meta.env) {
  if (!isDemoPaymentsEnabled(env)) return false;
  return normalizeCardNumber(cardNumber) === DEMO_CARD_NUMBER;
}

/** The card-number hint shown on the card form, or undefined (no hint). */
export function demoCardHelperText(env = import.meta.env) {
  return isDemoPaymentsEnabled(env) ? 'Demo: use 4242 4242 4242 4242' : undefined;
}
